import React, { useState } from 'react';
import { X, Crown, Check, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_PLANS } from '../constants/subscription';
import { SubscriptionPlan, SubscriptionType } from '../types/subscription';
import { auth } from '../services/firebase';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, currentPlan }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<'monthly' | 'yearly' | null>(null);

  if (!isOpen) return null;

  const premiumMonthly = SUBSCRIPTION_PLANS.find(plan => plan.type === SubscriptionType.PREMIUM_MONTHLY);
  const premiumYearly = SUBSCRIPTION_PLANS.find(plan => plan.type === SubscriptionType.PREMIUM_YEARLY);

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (plan.type === SubscriptionType.FREE) return;

    const planParam = plan.type === SubscriptionType.PREMIUM_YEARLY ? 'yearly' : 'monthly';
    setLoading(planParam);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Utilisateur non connecté');

      const apiBase = (import.meta as any).env?.VITE_STRIPE_API_BASE_URL || 'https://api-anivista-dev.poffet.me';
      const idToken = await user.getIdToken();

      const resp = await fetch(`${apiBase}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ plan: planParam })
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Erreur API Stripe (${resp.status}): ${txt}`);
      }

      const data = (await resp.json()) as { url?: string; sessionId?: string };
      if (!data.url) throw new Error('URL de checkout manquante');

      window.location.href = data.url;
    } catch (error) {
      console.error('UpgradeModal subscribe error:', error);
      const msg = error instanceof Error ? error.message : String(error);
      window.alert(msg);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl animate-slide-up relative overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white relative">
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Crown size={32} className="text-white" />
            </div>
            <h2 className="text-3xl font-black mb-2">Passez à Premium</h2>
            <p className="text-white/90">Débloquez toutes les fonctionnalités d'Anivista</p>
          </div>
        </div>

        {/* Plans */}
        <div className="p-8">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Plan Mensuel */}
            {premiumMonthly && (
              <div className="border-2 border-gray-200 rounded-2xl p-6 hover:border-indigo-300 transition-colors">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{premiumMonthly.name}</h3>
                    <p className="text-gray-600 text-sm mt-1">{premiumMonthly.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-gray-900">{premiumMonthly.price}</div>
                    <div className="text-sm text-gray-500">{premiumMonthly.currency}/mois</div>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  {premiumMonthly.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check size={20} className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => handleSubscribe(premiumMonthly)}
                  disabled={loading === 'monthly'}
                  className="w-full bg-indigo-600 text-white py-3 rounded-2xl font-bold hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading === 'monthly' ? 'Chargement…' : 'Choisir ce plan'}
                </button>
              </div>
            )}

            {/* Plan Annuel */}
            {premiumYearly && (
              <div className="border-2 border-indigo-500 rounded-2xl p-6 relative bg-indigo-50/30">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                  <Star size={12} className="inline mr-1" />
                  LE PLUS POPULAIRE
                </div>
                
                <div className="flex justify-between items-start mb-4 mt-2">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{premiumYearly.name}</h3>
                    <p className="text-gray-600 text-sm mt-1">{premiumYearly.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-gray-900">{premiumYearly.price}</div>
                    <div className="text-sm text-gray-500">{premiumYearly.currency}/an</div>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  {premiumYearly.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check size={20} className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => handleSubscribe(premiumYearly)}
                  disabled={loading === 'yearly'}
                  className="w-full bg-indigo-600 text-white py-3 rounded-2xl font-bold hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading === 'yearly' ? 'Chargement…' : 'Choisir ce plan'}
                </button>
              </div>
            )}
          </div>

          {/* Garantie */}
          <div className="mt-8 p-4 bg-gray-50 rounded-2xl text-center">
            <p className="text-gray-600 text-sm">
              <strong>Garantie satisfait ou remboursé</strong> - Annulez à tout moment
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
